import React, { useState } from 'react';
import {
  useCreateCommissionFormulaMutation,
  useDeleteCommissionFormulaMutation,
  useGetCoefficientTables,
  useGetCommissionFormulas,
  useTestCommissionFormulaMutation,
} from '../api/react-query';
import {
  useGetDealerCategories,
  useGetDcs,
  useGetManufacturersList,
} from '../../reference/api/react-query';
import { buildFormulaPayload, expressionToString } from '../api/transformers';
import { fmtInt, fmtNum } from '../../../shared/utils/formatters';
import {
  SectionHeader,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  FormField,
  FormGrid,
  FormActions,
  Button,
  Input,
  Select,
  Badge,
  EmptyState,
} from '../../../shared/ui/composites';
import {
  FormulaChip,
  FormulaFieldButton,
  FormulaOperatorButton,
} from '../../../shared/ui/formula/FormulaChip';

const cardGrid = 'grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4';

const operatorsGrid =
  'grid grid-cols-[repeat(auto-fit,minmax(72px,1fr))] gap-2';

// Field definitions with Persian labels
const FIELD_DEFINITIONS = {
  // Basic Info
  dealer_code: { label: 'کد نماینده', category: 'اطلاعات پایه', type: 'text' },
  dealer_name: { label: 'نام نماینده', category: 'اطلاعات پایه', type: 'text' },
  dc_id: { label: 'شناسه DC', category: 'اطلاعات پایه', type: 'number' },
  dc_name: { label: 'نام DC', category: 'اطلاعات پایه', type: 'text' },
  manufacturer_code: { label: 'کد تولیدکننده', category: 'اطلاعات پایه', type: 'number' },
  manufacturer_name: { label: 'نام تولیدکننده', category: 'اطلاعات پایه', type: 'text' },
  sales_office_id: { label: 'شناسه دفتر فروش', category: 'اطلاعات پایه', type: 'number' },
  
  // Targets
  target_jalali: { label: 'هدف شمسی', category: 'اهداف', type: 'number' },
  target_gregorian: { label: 'هدف میلادی', category: 'اهداف', type: 'number' },
  target_active_customers: { label: 'هدف مشتریان فعال', category: 'اهداف', type: 'number' },
  
  // Sales Data (Cartons)
  gross_carton: { label: 'کارتن ناخالص', category: 'فروش (کارتن)', type: 'number' },
  net_carton: { label: 'کارتن خالص', category: 'فروش (کارتن)', type: 'number' },
  return_carton: { label: 'کارتن برگشتی', category: 'فروش (کارتن)', type: 'number' },
  percent_return_carton: { label: 'درصد کارتن برگشتی', category: 'فروش (کارتن)', type: 'number' },
  
  // Sales Data (Amount)
  net_amount: { label: 'مبلغ خالص', category: 'فروش (مبلغ)', type: 'number' },
  net_amount_direct: { label: 'مبلغ خالص مستقیم', category: 'فروش (مبلغ)', type: 'number' },
  net_amount_retail: { label: 'مبلغ خالص خرده فروشی', category: 'فروش (مبلغ)', type: 'number' },
  
  // Sales Data (Units & Weight)
  gross_unit: { label: 'واحد ناخالص', category: 'فروش (واحد و وزن)', type: 'number' },
  net_unit: { label: 'واحد خالص', category: 'فروش (واحد و وزن)', type: 'number' },
  sell_weight: { label: 'وزن فروش', category: 'فروش (واحد و وزن)', type: 'number' },
  
  // Customers
  brand_active_customers: { label: 'مشتریان فعال برند', category: 'مشتریان', type: 'number' },
  total_active_customers: { label: 'کل مشتریان فعال', category: 'مشتریان', type: 'number' },
  debits: { label: 'بدهکاران', category: 'مشتریان', type: 'number' },
};

// Mathematical operators
const OPERATORS = [
  { symbol: '+', label: 'جمع' },
  { symbol: '-', label: 'تفریق' },
  { symbol: '*', label: 'ضرب' },
  { symbol: '/', label: 'تقسیم' },
  { symbol: '(', label: 'پرانتز باز' },
  { symbol: ')', label: 'پرانتز بسته' },
];

// Coefficient variables
const VARIABLES = [
  { symbol: 'a', label: 'ضریب a' },
  { symbol: 'b', label: 'ضریب b' },
  { symbol: 'c', label: 'ضریب c' },
  { symbol: 'd', label: 'ضریب d' },
];

function FormulaTab() {
  const { data: formulas = [], refetch: reloadFormulas } = useGetCommissionFormulas();
  const { data: manufacturers = [] } = useGetManufacturersList();
  const { data: dcs = [] } = useGetDcs();
  const { data: dealerCategories = [] } = useGetDealerCategories();
  const { data: coefficientTables = [] } = useGetCoefficientTables();
  const createFormula = useCreateCommissionFormulaMutation();
  const deleteFormula = useDeleteCommissionFormulaMutation();
  const testFormula = useTestCommissionFormulaMutation();

  const [editingFormula, setEditingFormula] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [formulaName, setFormulaName] = useState('');
  const [formulaDescription, setFormulaDescription] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('all');
  const [selectedDc, setSelectedDc] = useState('all');
  const [selectedDealerType, setSelectedDealerType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCoefficientTable, setSelectedCoefficientTable] = useState('');
  const [formulaExpression, setFormulaExpression] = useState([]);
  const [searchField, setSearchField] = useState('');

  const handleAddField = (fieldKey) => {
    setFormulaExpression([...formulaExpression, { type: 'field', value: fieldKey, label: FIELD_DEFINITIONS[fieldKey].label }]);
  };

  const handleAddOperator = (operator) => {
    setFormulaExpression([...formulaExpression, { type: 'operator', value: operator }]);
  };

  const handleAddVariable = (variable) => {
    setFormulaExpression([...formulaExpression, { type: 'variable', value: variable, label: `{${variable}}` }]);
  };

  const handleAddNumber = () => {
    const number = prompt('عدد را وارد کنید:');
    if (number && !isNaN(number)) {
      setFormulaExpression([...formulaExpression, { type: 'number', value: parseFloat(number) }]);
    }
  };

  const handleRemoveItem = (index) => {
    setFormulaExpression(formulaExpression.filter((_, i) => i !== index));
  };

  const handleSaveFormula = async () => {
    if (!formulaName.trim()) {
      alert('لطفاً نام فرمول را وارد کنید');
      return;
    }

    if (formulaExpression.length === 0) {
      alert('لطفاً فرمول را تعریف کنید');
      return;
    }

    const expressionString = expressionToString(formulaExpression);

    const formulaData = buildFormulaPayload({
      id: editingFormula?.id || Date.now().toString(),
      name: formulaName,
      description: formulaDescription,
      manufacturer_code: selectedManufacturer === 'all' ? null : parseInt(selectedManufacturer, 10),
      dc_id: selectedDc === 'all' ? null : parseInt(selectedDc, 10),
      dealer_type: selectedDealerType === 'all' ? null : selectedDealerType,
      category: selectedCategory === 'all' ? null : selectedCategory,
      coefficient_table: selectedCoefficientTable || null,
      expression: formulaExpression,
      expression_string: expressionString,
      created_at: editingFormula?.created_at,
    });

    try {
      await createFormula.mutateAsync(formulaData);
      alert('فرمول با موفقیت ذخیره شد');
      reloadFormulas();
      handleCancelEdit();
    } catch (error) {
      console.error('Error saving formula:', error);
      alert('خطا در ذخیره فرمول');
    }
  };

  const handleEditFormula = (formula) => {
    setEditingFormula(formula);
    setFormulaName(formula.name);
    setFormulaDescription(formula.description || '');
    setSelectedManufacturer(formula.manufacturer_code?.toString() || 'all');
    setSelectedDc(formula.dc_id?.toString() || 'all');
    setSelectedDealerType(formula.dealer_type || 'all');
    setSelectedCategory(formula.category || 'all');
    setSelectedCoefficientTable(formula.coefficient_table || '');
    setFormulaExpression(formula.expression || []);
    setShowBuilder(true);
  };

  const handleDeleteFormula = async (formulaId) => {
    if (!window.confirm('آیا از حذف این فرمول اطمینان دارید؟')) {
      return;
    }

    try {
      await deleteFormula.mutateAsync(formulaId);
      alert('فرمول با موفقیت حذف شد');
      reloadFormulas();
    } catch (error) {
      console.error('Error deleting formula:', error);
      alert('خطا در حذف فرمول');
    }
  };

  const handleCancelEdit = () => {
    setEditingFormula(null);
    setFormulaName('');
    setFormulaDescription('');
    setSelectedManufacturer('all');
    setSelectedDc('all');
    setSelectedDealerType('all');
    setSelectedCategory('all');
    setSelectedCoefficientTable('');
    setFormulaExpression([]);
    setShowBuilder(false);
  };

  const handleTestFormula = async () => {
    if (formulaExpression.length === 0) {
      alert('لطفاً ابتدا فرمول را تعریف کنید');
      return;
    }

    try {
      const result = await testFormula.mutateAsync({
        expression: formulaExpression,
        manufacturer_code: selectedManufacturer === 'all' ? null : parseInt(selectedManufacturer, 10),
        dc_id: selectedDc === 'all' ? null : parseInt(selectedDc, 10),
        dealer_type: selectedDealerType === 'all' ? null : selectedDealerType,
        category: selectedCategory === 'all' ? null : selectedCategory,
        coefficient_table: selectedCoefficientTable || null,
      });

      alert(`نتایج تست:\n\nتعداد رکوردها: ${fmtInt(result.count)}\nنمونه محاسبات اولین 5 رکورد:\n\n${result.samples.map(s =>
        `نماینده: ${s.dealer_name}\nنتیجه: ${fmtNum(s.result)}`
      ).join('\n\n')}`);
    } catch (error) {
      console.error('Error testing formula:', error);
      alert(`خطا در تست فرمول:\n${error.response?.data?.error || error.message}`);
    }
  };

  // Group fields by category
  const fieldsByCategory = Object.entries(FIELD_DEFINITIONS).reduce((acc, [key, value]) => {
    if (!acc[value.category]) {
      acc[value.category] = [];
    }
    acc[value.category].push({ key, ...value });
    return acc;
  }, {});

  // Filter fields by search
  const filteredFields = searchField
    ? Object.entries(FIELD_DEFINITIONS)
        .filter(([key, value]) => 
          value.label.includes(searchField) || 
          key.toLowerCase().includes(searchField.toLowerCase())
        )
        .reduce((acc, [key, value]) => {
          if (!acc[value.category]) {
            acc[value.category] = [];
          }
          acc[value.category].push({ key, ...value });
          return acc;
        }, {})
    : fieldsByCategory;

  return (
    <div className="w-full min-w-0">
      {!showBuilder ? (
        <div className="w-full min-w-0">
          <SectionHeader
            title="فرمول‌های پورسانت"
            action={
              <Button variant="add" onClick={() => setShowBuilder(true)}>
                ایجاد فرمول جدید
              </Button>
            }
          />

          <div className={cardGrid}>
            {formulas.length === 0 ? (
              <EmptyState
                className="col-span-full"
                message="هیچ فرمولی تعریف نشده است"
                actionLabel="ایجاد اولین فرمول"
                onAction={() => setShowBuilder(true)}
              />
            ) : (
              formulas.map(formula => (
                <Card key={formula.id} variant="panel">
                  <CardHeader>
                    <CardTitle className="mb-3">{formula.name}</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="icon"
                        size="icon"
                        onClick={() => handleEditFormula(formula)}
                        title="ویرایش"
                      >
                        ✏️
                      </Button>
                      <Button
                        variant="icon"
                        size="icon"
                        onClick={() => handleDeleteFormula(formula.id)}
                        title="حذف"
                      >
                        🗑️
                      </Button>
                    </div>
                  </CardHeader>
                  {formula.description && (
                    <CardDescription>{formula.description}</CardDescription>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="scope">
                      تولیدکننده: {formula.manufacturer_code 
                        ? (manufacturers.find(m => m.manufacturer_code === formula.manufacturer_code)?.manufacturer_name || 
                           manufacturers.find(m => m.ManufacturerCode === formula.manufacturer_code)?.ManufacturerName || 
                           formula.manufacturer_code)
                        : 'همه'}
                    </Badge>
                    <Badge variant="scope">
                      DC: {formula.dc_id 
                        ? (dcs.find(d => d.dc_id === formula.dc_id)?.dc_name || formula.dc_id)
                        : 'همه'}
                    </Badge>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {formula.expression?.map((item, idx) => (
                      <FormulaChip
                        key={idx}
                        className="bg-[#eef2ff] !text-[#3730a3]"
                      >
                        {item.type === 'field' ? item.label : item.value}
                      </FormulaChip>
                    ))}
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="w-full min-w-0">
          <SectionHeader
            title={editingFormula ? 'ویرایش فرمول' : 'ایجاد فرمول جدید'}
            action={
              <Button variant="cancel" onClick={handleCancelEdit}>
                بازگشت
              </Button>
            }
          />

          <div className="flex flex-col gap-4">
            <Card variant="panel">
              <CardTitle className="mb-3">اطلاعات فرمول و شرایط اعمال</CardTitle>
              <FormGrid className="mb-0">
                <FormField label="نام فرمول" required>
                  <Input
                    variant="form"
                    type="text"
                    value={formulaName}
                    onChange={(e) => setFormulaName(e.target.value)}
                    placeholder="مثال: فرمول پورسانت فروش"
                  />
                </FormField>
                <FormField label="توضیحات">
                  <Input
                    variant="form"
                    type="text"
                    value={formulaDescription}
                    onChange={(e) => setFormulaDescription(e.target.value)}
                    placeholder="توضیحات اختیاری"
                  />
                </FormField>
                <FormField label="جدول ضرایب">
                  <Select
                    value={selectedCoefficientTable}
                    onChange={(e) => setSelectedCoefficientTable(e.target.value)}
                  >
                    <option value="">بدون ضریب</option>
                    {coefficientTables.map(table => (
                      <option key={table.id} value={table.id}>
                        {table.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="نوع نماینده">
                  <Select
                    value={selectedDealerType}
                    onChange={(e) => setSelectedDealerType(e.target.value)}
                  >
                    <option value="all">همه انواع</option>
                    {dealerCategories.map(cat => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
              </FormGrid>
              <FormGrid className="mb-0 mt-4">
                <FormField label="دسته‌بندی">
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">همه دسته‌ها</option>
                    <option value="عادی">عادی</option>
                    <option value="عمده">عمده</option>
                    <option value="ویژه">ویژه</option>
                    <option value="جنوب عادی">جنوب عادی</option>
                    <option value="تهران">تهران</option>
                    <option value="خوزستان">خوزستان</option>
                    <option value="شیراز">شیراز</option>
                    <option value="اصفهان">اصفهان</option>
                  </Select>
                </FormField>
                <FormField label="تولیدکننده">
                  <Select
                    value={selectedManufacturer}
                    onChange={(e) => setSelectedManufacturer(e.target.value)}
                  >
                    <option value="all">همه تولیدکنندگان</option>
                    {manufacturers.map(m => {
                      const code = m.manufacturer_code || m.ManufacturerCode;
                      const name = m.manufacturer_name || m.ManufacturerName;
                      return (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      );
                    })}
                  </Select>
                </FormField>
                <FormField label="DC">
                  <Select
                    value={selectedDc}
                    onChange={(e) => setSelectedDc(e.target.value)}
                  >
                    <option value="all">همه DC ها</option>
                    {dcs.map(dc => (
                      <option key={dc.dc_id} value={dc.dc_id}>
                        {dc.dc_name}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="آینده" className="opacity-50">
                  <Select disabled>
                    <option>برای گسترش بیشتر...</option>
                  </Select>
                </FormField>
              </FormGrid>
            </Card>

            <div className="grid grid-cols-[minmax(260px,360px)_minmax(0,1fr)] gap-4 items-start max-lg:grid-cols-1">
              <div className="flex flex-col gap-4 min-w-0 max-lg:grid max-lg:grid-cols-2 max-md:grid-cols-1">
                <Card variant="panel">
                  <CardTitle className="mb-3">عملگرها و ضرایب</CardTitle>
                  <div className={operatorsGrid}>
                    {OPERATORS.map(op => (
                      <FormulaOperatorButton
                        key={op.symbol}
                        onClick={() => handleAddOperator(op.symbol)}
                        title={op.label}
                      >
                        {op.symbol}
                      </FormulaOperatorButton>
                    ))}
                    <FormulaOperatorButton
                      onClick={handleAddNumber}
                      title="عدد ثابت"
                    >
                      123
                    </FormulaOperatorButton>
                  </div>
                  {selectedCoefficientTable && (
                    <>
                      <h4 className="mt-4 mb-2.5 text-sm text-[#666]">
                        متغیرهای ضریب
                      </h4>
                      <div className={operatorsGrid}>
                        {VARIABLES.map(v => (
                          <FormulaOperatorButton
                            key={v.symbol}
                            className="bg-[#ecfeff] !text-[#0e7490]"
                            onClick={() => handleAddVariable(v.symbol)}
                            title={v.label}
                          >
                            {v.symbol}
                          </FormulaOperatorButton>
                        ))}
                      </div>
                    </>
                  )}
                </Card>

                <Card variant="panel">
                  <CardTitle className="mb-3">فیلدهای قابل استفاده</CardTitle>
                  <Input
                    variant="search"
                    type="text"
                    className="mb-3"
                    placeholder="جستجوی فیلد..."
                    value={searchField}
                    onChange={(e) => setSearchField(e.target.value)}
                  />
                  <div className="max-h-[460px] overflow-y-auto pl-1 max-md:max-h-[320px]">
                    {Object.entries(filteredFields).map(([category, fields]) => (
                      <div key={category} className="mb-4">
                        <h4 className="!text-[#64748b] text-sm mb-2">{category}</h4>
                        <div className={operatorsGrid}>
                          {fields.map(field => (
                            <FormulaFieldButton
                              key={field.key}
                              onClick={() => handleAddField(field.key)}
                              title={`${field.label} (${field.key})`}
                            >
                              {field.label}
                              <span className="!text-[#64748b] ltr text-xs">{field.key}</span>
                            </FormulaFieldButton>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              <div className="min-w-0">
                <Card variant="panel">
                  <CardTitle className="mb-3">فرمول</CardTitle>
                  <div className="min-h-[260px] border border-dashed border-[#cbd5e1] rounded-xl p-4">
                    {formulaExpression.length === 0 ? (
                      <div className="flex min-h-56 items-center justify-center !text-[#94a3b8] text-center">
                        فیلدها و عملگرها را از سمت راست انتخاب کنید
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {formulaExpression.map((item, index) => (
                          <FormulaChip
                            as="div"
                            key={index}
                            className={
                              item.type === 'variable'
                                ? 'bg-[#ecfeff] !text-[#0e7490]'
                                : 'bg-[#eef2ff] !text-[#3730a3]'
                            }
                          >
                            <span className="flex flex-col gap-0.5">
                              {item.type === 'field' ? (
                                <>
                                  <span>{item.label}</span>
                                  <span className="!text-[#64748b] ltr text-xs">{item.value}</span>
                                </>
                              ) : item.type === 'variable' ? (
                                <>
                                  <span>{item.label}</span>
                                  <span className="!text-[#64748b] ltr text-xs">متغیر ضریب</span>
                                </>
                              ) : (
                                item.value
                              )}
                            </span>
                            <Button
                              variant="icon"
                              size="icon"
                              className="h-6 w-6 min-h-0"
                              onClick={() => handleRemoveItem(index)}
                              title="حذف"
                            >
                              ×
                            </Button>
                          </FormulaChip>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </div>

            <FormActions>
              <Button variant="submit" onClick={handleSaveFormula}>
                ذخیره فرمول
              </Button>
              <Button variant="secondary" onClick={handleTestFormula}>
                تست فرمول
              </Button>
              <Button variant="cancel" onClick={handleCancelEdit}>
                انصراف
              </Button>
            </FormActions>
          </div>
        </div>
      )}
    </div>
  );
}

export default FormulaTab;
